// @owners { team: patients-team }
import { SIZES, SPACING } from '@alto/design-library-tokens';
import {
  ActionSheetContext,
  Border,
  Button,
  Card,
  CardHeader,
  LgPadding,
  LgSpacing,
  ListBase,
  MdSpacing,
  XlSpacing,
} from '@alto/design-system';
import { Experimentation } from '@alto/experimentation';
import {
  AddOnsContext,
  CSATActionSheet,
  CalendarRow,
  CalendarRowV2,
  DeliveryCardCTA,
  LocationRow,
  MedRow,
  useCanShowCSAT,
} from '@alto/features';
import React, { useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { push } from 'react-router-redux';
import { IMG_ALT_TEXTS } from '~shared/constants';
import { type APIOrderMethod } from '~shared/features/delivery-info/types';
import { ORIGINS } from '~shared/features/essentials/add_ons_consts';
import { getUpcomingOrderLabel } from '~shared/features/homescreen/utils';
import { getWhatToExpectCopy } from '~shared/features/shipments/helpers/getWhatToExpectCopy';
import { getShipmentAddress, getShipmentWithID } from '~shared/features/shipments/selectors/getShipments';
import { getShipmentWithItemsByID } from '~shared/features/shipments/selectors/getShipmentsWithItems';
import { formatFullAddress } from '~shared/helpers/address';
import { isPickup } from '~shared/helpers/order';
import { getRouteState } from '~shared/selectors/getRouteState';
import { useDispatchShared, useSelectorShared } from '~shared/store';
import { Products } from './Products';
import { FullPage } from '~web/features/essentials/components';
import { MobileProducts } from '~web/features/essentials/pages/OrderSuccessWithStoreProducts/MobileProducts';
import { Icon, Image, Text, View } from '~web/features/essentials/primitives';
import { TransferUpsellActionSheet } from '~web/features/transfers/components/TransferUpsellActionSheet';
import { useDeprecatedMediaQuery, useDeprecatedWindowResize } from '~web/hooks';

const orderCompleteImage = 'https://assets.prod.alto.com/otc_categories/box.svg';

type Props = {
  readonly params: {
    shipmentID: string;
  };
};

const ORIGIN = ORIGINS.ORDER_SUCCESS;
// eslint-disable-next-line sonarjs/cognitive-complexity
export const OrderSuccessWithStoreProducts = ({ params: { shipmentID } }: Props) => {
  const { value: isSelfScheduledPickupEnabled } = Experimentation.useFeatureFlag('self_scheduled_pickup_fe');
  const { value: isV2CalendarRowEnabled } = Experimentation.useFeatureFlag('v2_calendar_row');
  const routeState = useSelectorShared(getRouteState);
  const newPrescriptionIDs = routeState?.bundledPrescriptionIDs || [];
  const shipment = useSelectorShared((state) => getShipmentWithID(state, { shipmentID: Number(shipmentID) }));
  const addOnsContext = useMemo(() => ({ shipmentID: shipment?.id, origin: ORIGIN }) as const, [shipment?.id]);
  const shipmentWithItems = useSelectorShared((state) =>
    getShipmentWithItemsByID(state, {
      id: Number(shipmentID),
    }),
  );
  const address = useSelectorShared((state) => getShipmentAddress(state, { shipmentID: Number(shipmentID) }));
  const medicationNames = shipmentWithItems
    ? shipmentWithItems.items.map((item) => {
        if (!newPrescriptionIDs.includes(item.prescription.id) && item.medication) {
          return item.medication.medicationName;
        }
        return '';
      })
    : [];
  const newMedicationNames = newPrescriptionIDs
    ? newPrescriptionIDs.map((id: number) => {
        const item = shipmentWithItems?.items.find((item) => item.prescription.id === id);
        return item?.medication ? item.medication.medicationName : item?.prescription.medication_name;
      })
    : [];
  const dispatch = useDispatchShared();
  const isMobile = !useDeprecatedMediaQuery('SM');
  const isLargeScreen = useDeprecatedMediaQuery('LG');
  const screenPartitions = isMobile ? '100%' : '50%';
  const windowSize = useDeprecatedWindowResize();
  const defaultViewSidePadding = isMobile ? SPACING.STATIC.LG.px : SPACING.STATIC.XXL.px;
  const sidePadding = isLargeScreen
    ? `${(windowSize.width - parseFloat(SIZES.PAGE.WEB_NAVBAR_WIDTH.LG)) / 2}px`
    : defaultViewSidePadding;
  const { setActiveActionSheet } = useContext(ActionSheetContext);
  const { canShowCSAT, updateCSATViewedDate } = useCanShowCSAT();

  const handleNavToEssentials = () => {
    dispatch(push({ pathname: '/store', search: `?order=${shipmentID}&origin=${ORIGIN}` }));
  };

  const timer = useRef<NodeJS.Timeout | null>(null);
  useEffect(() => {
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, []);

  const showCSAT = useCallback(() => {
    if (canShowCSAT) {
      updateCSATViewedDate();
      if (!timer.current) {
        timer.current = setTimeout(() => {
          setActiveActionSheet(<CSATActionSheet />);
        }, 2000);
      }
    }
  }, [canShowCSAT, setActiveActionSheet, updateCSATViewedDate]);
  const showTransferUpsell = useCallback(() => {
    if (!canShowCSAT) {
      setActiveActionSheet(<TransferUpsellActionSheet />);
    }
  }, [canShowCSAT, setActiveActionSheet]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    showCSAT();
    showTransferUpsell();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const rows = [
    {
      key: 'calendar',
      component: isV2CalendarRowEnabled ? (
        <CalendarRowV2
          date={shipment?.scheduled_date}
          startTime={shipment?.scheduled_deliver_after}
          endTime={shipment?.scheduled_deliver_before}
          method={shipment?.shipment_method}
          status={shipment?.status}
          origin="order_success"
        />
      ) : (
        <CalendarRow
          date={shipment?.scheduled_date}
          startTime={shipment?.scheduled_deliver_after}
          endTime={shipment?.scheduled_deliver_before}
          isPickup={isSelfScheduledPickupEnabled && isPickup(shipment?.shipment_method)}
        />
      ),
    },
    {
      key: 'location',
      component: (
        <LocationRow
          address={formatFullAddress(address)}
          isPickup={shipment?.is_pickup}
          showMap={shipment?.status !== 'delivered'}
        />
      ),
    },
    {
      key: 'MedRow',
      component: (
        <MedRow
          medicationNames={medicationNames}
          newMedicationNames={newMedicationNames}
          showAllMeds
        />
      ),
    },
  ];

  return (
    <FullPage>
      <AddOnsContext.Provider value={addOnsContext}>
        <View
          padding={`${SPACING.STATIC.XXXL.px} ${defaultViewSidePadding} ${SPACING.STATIC.XXXL.px} ${sidePadding}`}
          flexBasis={screenPartitions}
          flexDirection="column"
          backgroundColor="TERTIARY_LIGHTEST"
        >
          <View
            alignItems={isMobile ? 'center' : 'flex-start'}
            flexDirection="column"
          >
            {isMobile ? (
              <View justifyContent="center">
                <Image
                  src={orderCompleteImage}
                  alt="order complete"
                  width={SIZES.ILLUSTRATION.XL.px}
                  height={SIZES.ILLUSTRATION.XL.px}
                />
              </View>
            ) : null}
            <LgSpacing />
            <Text
              title="Order scheduled!"
              variant="h1"
              align="center"
            />
            <XlSpacing />
          </View>
          <Card>
            <CardHeader
              title={getUpcomingOrderLabel({
                isPickup: isPickup(shipment?.shipment_method as APIOrderMethod | undefined),
              })}
              description={getWhatToExpectCopy(shipment?.shipment_method)}
              illustrationSrc={isMobile ? undefined : { uri: orderCompleteImage }}
              illustrationAccessibilityLabel={IMG_ALT_TEXTS.orderBox}
            />

            {shipment ? (
              <>
                <ListBase items={rows} />
                <MdSpacing />
                <LgPadding topPadding={SPACING.STATIC.NONE}>
                  <DeliveryCardCTA shipmentID={shipmentID} />
                </LgPadding>
              </>
            ) : (
              <View>
                <Icon
                  name="loader"
                  spinning
                  interactive={false}
                  label="loading"
                  size="LG"
                />
              </View>
            )}
          </Card>
          {isMobile ? (
            <>
              <MdSpacing />
              {shipment ? <MobileProducts /> : null}
            </>
          ) : null}
        </View>
        {!isMobile && (
          <View
            flexDirection="column"
            padding={`${SPACING.STATIC.XXXL.px} ${sidePadding} ${SPACING.STATIC.XXXL.px} ${SPACING.STATIC.XXL.px}`}
            flexBasis={screenPartitions}
            overflow="hidden"
          >
            <View
              justifyContent="space-between"
              alignItems="center"
            >
              <View flexDirection="column">
                <View alignItems="center">
                  <Text
                    title="Add "
                    variant="h2"
                  />
                  <Image
                    src="https://assets.prod.alto.com/otc_categories/essentials_logo.svg"
                    alt="add essentials to your order"
                  />
                </View>
                <Text
                  title=" to your order"
                  variant="h2"
                />
              </View>
              {shipment ? (
                <Button
                  label="See all products"
                  onPress={handleNavToEssentials}
                />
              ) : null}
            </View>
            <LgSpacing />
            <Border fullWidth />
            <LgSpacing />
            {shipment ? <Products /> : null}
          </View>
        )}
      </AddOnsContext.Provider>
    </FullPage>
  );
};
